<template>
    <q-card
        :id="id"
        ref="$self"
        v-ripple:orange-5="focusRipplesEnabled"
        flat
        square
        class="form-section relative-position"
        @ripple="onRipple"
    >
        <div class="flex justify-between">
            <div v-if="title" class="form-section-title row items-center q-px-lg">
                <div v-if="!noCollapse" class="col-auto">
                    <q-btn
                        flat
                        no-caps
                        class="q-px-sm"
                        color="primary"
                        :icon="shown ? 'expand_less' : 'expand_more'"
                        @click="setVisible(!shown)"
                    />
                </div>
                <div>
                    <div class="text-h4 text-weight-medium text-primary flex items-center">
                        <q-icon
                            v-if="icon"
                            :name="icon"
                            class="q-mr-sm q-ml-md"
                            size="24px"
                        />

                        <span v-if="sectionNumber">{{ sectionNumber }}. &nbsp;</span>

                        <router-link v-if="to" :to="to" class="text-primary">
                            {{ title }}
                        </router-link>

                        <span v-else>{{ title }}</span>

                        <form-tooltip-icon v-if="tooltip" size="24px">
                            <div v-if="tooltipUseHtmlStyle" v-html="tooltip" />
                            <div v-else>
                                {{ tooltip }}
                            </div>
                        </form-tooltip-icon>
                    </div>

                    <div v-if="subtitle" class="text-subtitle">
                        {{ subtitle }}
                    </div>
                </div>
            </div>
            <div class="row items-center q-px-lg">
                <slot name="heading-content" />
            </div>
        </div>
        <div v-if="shown" :class="contentClasses" class="q-gutter-y-lg">
            <slot />
        </div>
    </q-card>
</template>

<script setup>
import { nextTick, ref, watch } from 'vue'
import { defaultTo } from 'lodash-es'

const props = defineProps({
    id: String,
    title: String,
    icon: String,
    tooltip: String,
    tooltipUseHtmlStyle: Boolean, // use v-html to display tooltip, etc <br> or special html element
    subtitle: String,
    collapsed: Boolean, // Hint that this section should be initially collapsed
    visibilityMap: Object, // An optional special object that tracks the visibility of this section and persists it.
    noCollapse: Boolean, // Hide the manual collapse button?
    edgeToEdge: Boolean, // Remove horizontal padding on content container
    to: [String, Object], // Turn the title into a clickable router link
    sectionNumber: {
        type: Number,
        default: null,
    },
})

const $self = ref()

const isCollapsedByDefault = props.visibilityMap
    ? defaultTo(props.visibilityMap.collapse_sections[props.id], defaultTo(props.visibilityMap.collapse_all, props.collapsed))
    : props.collapsed

const shown = ref(!isCollapsedByDefault)

const focusRipplesEnabled = ref(false)

const contentClasses = {
    'q-pb-lg': props.title,
    'q-px-lg': !props.edgeToEdge,
}

function onRipple() {
    focusRipplesEnabled.value = true
    nextTick(() => {
        if ($self.value && $self.value.$el) { // component may have been unmounted
            $self.value.$el.dispatchEvent(new Event('click'))
        }

        focusRipplesEnabled.value = false
    })
}

function setVisible(isShown) {
    shown.value = isShown

    // Remember this new visibility state made by the user?
    if (props.id && props.visibilityMap) {
        props.visibilityMap.collapse_sections[props.id] = !isShown
    }
}

if (props.visibilityMap) {
    watch(() => props.visibilityMap.collapse_all, (val) => shown.value = !val)
}
</script>

<style lang="scss">
    .form-section {
        border: 1px solid $input-color;
        .form-section-title { min-height: 80px }
    }

    .form-section + .form-section { margin-top: 5px }

    // Remove extra horizontal padding on small screens
    @media (max-width: $breakpoint-sm) {
        .form-section {
            padding-left: 0;
            padding-right: 0;
        }
    }
</style>
